import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { getMyProfile } from "../../../../core/data/redux/employeesSlice";
import { toast } from "react-toastify";
import { submitStaffQuarterVacation, addVacationIntimation, getMyAllotments, getAllVacationIntimations, getVacationIntimationData } from "../../../../core/data/redux/staffQuarterSlice";
import { formatFlatNoWithBlock } from "../staffQuarterUtils";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

interface VacationIntimationFormProps {
  embedded?: boolean;
  onSuccess?: () => void;
}

const VacationIntimationForm: React.FC<VacationIntimationFormProps> = ({ embedded = false, onSuccess }) => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const employeesState = useSelector((s: any) => s.employeesState);
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  const user = employeesState?.data?.data;
  const allotments = staffQuarterState?.myAllotments?.data || [];

  const extractIntimationsArray = (state: any): any[] => {
    const sources = [
      state?.vacationIntimationData,
      state?.allVacations,
      state?.vacationRequests
    ];

    for (const raw of sources) {
      if (!raw) continue;
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.data)) return raw.data;
      if (Array.isArray(raw?.result)) return raw.result;
      if (Array.isArray(raw?.items)) return raw.items;
      if (raw?.data?.data && Array.isArray(raw.data.data)) return raw.data.data;
      if (typeof raw === "object" && (raw.id || raw.vacationDate || raw.vacationIntimationDate)) return [raw];
    }
    return [];
  };

  const existingIntimations = extractIntimationsArray(staffQuarterState);

  const toInputDateFormat = (dateStr: any) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      const parts = String(dateStr).split("T")[0];
      return parts.includes("-") ? parts : "";
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [selectedAllotment, setSelectedAllotment] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    vacationDate: "",
    reasonDetails: "",
    undertaking: false,
  });

  // Find matching intimation for selected allotment
  const matchedIntimation = selectedAllotment
    ? existingIntimations.find((item: any) =>
        String(item.allotmentId || item.id) === String(selectedAllotment.id || selectedAllotment.allotmentId) ||
        String(item.registrationId) === String(selectedAllotment.registrationId || selectedAllotment.id) ||
        String(item.flatNumber || item.flatNo) === String(selectedAllotment.flatNumber || selectedAllotment.flatNo) ||
        String(item.allotmentReferenceNumber || item.referenceNumber) === String(selectedAllotment.referenceNumber || selectedAllotment.allotmentReferenceNumber)
      ) || existingIntimations[0]
    : existingIntimations[0];

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(getMyAllotments({}));
    dispatch(getVacationIntimationData({}));
    dispatch(getAllVacationIntimations({}));
  }, [dispatch]);

  useEffect(() => {
    if (matchedIntimation) {
      const rawDate = matchedIntimation.vacationDate || matchedIntimation.vacationIntimationDate || matchedIntimation.tentativeVacationDate;
      const formattedDate = toInputDateFormat(rawDate);
      const reason = matchedIntimation.reasonDetails || matchedIntimation.reasonForVacation || matchedIntimation.vacationIntimationReason || matchedIntimation.remarks || "";

      setForm({
        vacationDate: formattedDate,
        reasonDetails: reason,
        undertaking: true,
      });
    }
  }, [matchedIntimation]);

  useEffect(() => {
    if (allotments.length > 0 && !selectedAllotment) {
      setSelectedAllotment(allotments[0]);
    }
  }, [allotments, selectedAllotment]);

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as any;
    const name = target.name;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAllotment) {
      toast.error("No active quarter allotment selected");
      return;
    }

    if (!form.vacationDate) {
      toast.error("Please specify the Date of Vacation");
      return;
    }

    if (!form.reasonDetails) {
      toast.error("Please provide a reason for vacation");
      return;
    }

    if (!form.undertaking) {
      toast.error("Please accept the undertaking declaration to proceed");
      return;
    }

    // Validation date check
    const occDateStr =
      selectedAllotment.occupiedDate ||
      selectedAllotment.occupationDate ||
      selectedAllotment.occupationdate;
    if (occDateStr) {
      const occDate = new Date(occDateStr);
      occDate.setHours(0, 0, 0, 0);
      const vacDate = new Date(form.vacationDate);
      vacDate.setHours(0, 0, 0, 0);

      if (vacDate <= occDate) {
        toast.error("Vacation Date must be after the Occupation Date");
        return;
      }
    }

    setIsSubmitting(true);

    const parsedFlatId = Number(
      selectedAllotment?.flatDetails?.id ||
      selectedAllotment?.flat?.id ||
      (selectedAllotment?.flatId && !isNaN(Number(selectedAllotment.flatId)) ? selectedAllotment.flatId : null) ||
      selectedAllotment?.allotmentId ||
      selectedAllotment?.id ||
      0
    );

    const payload = {
      registrationId: Number(selectedAllotment?.registrationId || selectedAllotment?.id || 1),
      registrationReferenceNumber: String(
        selectedAllotment?.registrationReferenceNumber || selectedAllotment?.registrationId || "SQ-2026-0001"
      ),
      allotmentId: Number(selectedAllotment?.id || selectedAllotment?.allotmentId || selectedAllotment?.registrationId || 1),
      allotmentReferenceNumber: String(
        selectedAllotment?.referenceNumber || selectedAllotment?.allotmentReferenceNumber || selectedAllotment?.registrationReferenceNumber || "ALT-2026-0012"
      ),
      empId: String(user?.empId || user?.employeeId || user?.employeeNumber || user?.id || "00012345"),
      flatId: parsedFlatId,
      flatNumber: String(selectedAllotment?.flatNumber || selectedAllotment?.flatNo || ""),
      locationId: Number(selectedAllotment?.locationId || selectedAllotment?.location?.id || 1),
      locationName: String(selectedAllotment?.location || selectedAllotment?.locationName || ""),
      quarterType: String(selectedAllotment?.quarterType || ""),
      quarterTypeCode: Number(selectedAllotment?.quarterTypeCode || selectedAllotment?.quarterTypeId || 2),
      pool: String(selectedAllotment?.pool || "General"),
      poolId: Number(selectedAllotment?.poolId || 1),
      vacationDate: form.vacationDate,
      vacationIntimationDate: form.vacationDate,
      reasonDetails: form.reasonDetails,
      reasonForVacation: form.reasonDetails,
      vacationIntimationReason: form.reasonDetails,
      remarks: form.reasonDetails || "Submitting early notice for vacation",
      status: "INTIMATED"
    };

    dispatch(addVacationIntimation(payload))
      .then((res: any) => {
        setIsSubmitting(false);
        if (res.payload?.success || res.payload?.status === 200 || res.payload?.status === 201) {
          toast.success("Vacation Intimation Form submitted successfully!");
          setForm({ vacationDate: "", reasonDetails: "", undertaking: false });
          dispatch(getVacationIntimationData({}));
          dispatch(getAllVacationIntimations({}));
          setShowForm(false);
          if (onSuccess) {
            onSuccess();
          }
        } else {
          toast.error(res.payload?.message || "Failed to submit vacation intimation.");
        }
      })
      .catch((err: any) => {
        setIsSubmitting(false);
        toast.error("An error occurred while submitting.");
      });
  };

  const hasSubmittedIntimations = existingIntimations && existingIntimations.length > 0;
  const isDisplayingTable = hasSubmittedIntimations && !showForm;

  const tableView = (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px", backgroundColor: "#fff" }}>
      <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ borderRadius: "14px 14px 0 0" }}>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success me-1" style={{ width: "38px", height: "38px", backgroundColor: "rgba(25, 135, 84, 0.1)" }}>
            <i className="ti ti-circle-check text-success" style={{ fontSize: "20px" }}></i>
          </div>
          <div>
            <h5 className="card-title mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>Submitted Vacation Intimation Requests</h5>
            <span className="text-muted small">Below are your submitted formal vacation intimation details</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-danger btn-sm px-3 fw-bold d-flex align-items-center gap-1 shadow-xs"
            onClick={() => setShowForm(true)}
          >
            <i className="ti ti-plus"></i> Submit New Intimation
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-3" style={{ fontSize: "12px", textTransform: "uppercase" }}>Ref Number</th>
                <th style={{ fontSize: "12px", textTransform: "uppercase" }}>Flat & Location</th>
                <th style={{ fontSize: "12px", textTransform: "uppercase" }}>Quarter Type</th>
                <th style={{ fontSize: "12px", textTransform: "uppercase" }}>Vacation Date</th>
                <th style={{ fontSize: "12px", textTransform: "uppercase" }}>Reason</th>
                <th className="text-center" style={{ fontSize: "12px", textTransform: "uppercase" }}>Status</th>
                <th className="text-end pe-3" style={{ fontSize: "12px", textTransform: "uppercase" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {existingIntimations.map((item: any, idx: number) => {
                const refNo = item.allotmentReferenceNumber || item.registrationReferenceNumber || item.referenceNumber || `INT-${item.id || idx + 1}`;
                const flatStr = formatFlatNoWithBlock(item) || item.flatNumber || item.flatNo || "-";
                const locationStr = item.locationName || item.location || "-";
                const qType = item.quarterType || "-";
                const vacDate = item.vacationDate || item.tentativeVacationDate || item.vacationIntimationDate;
                const reasonStr = item.reasonDetails || item.reasonForVacation || item.vacationIntimationReason || item.remarks || "-";
                const statusStr = (item.status || "INTIMATED").toUpperCase();

                return (
                  <tr key={item.id || idx}>
                    <td className="ps-3 fw-bold text-primary" style={{ fontSize: "13px" }}>{refNo}</td>
                    <td>
                      <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>Flat {flatStr}</div>
                      <div className="text-muted small" style={{ fontSize: "11px" }}>{locationStr}</div>
                    </td>
                    <td><span className="badge bg-light text-dark border px-2 py-1">{qType}</span></td>
                    <td>
                      <div className="d-flex align-items-center gap-1 text-dark fw-medium" style={{ fontSize: "13px" }}>
                        <i className="ti ti-calendar-event text-danger me-1"></i>
                        {formatDate(vacDate)}
                      </div>
                    </td>
                    <td style={{ maxWidth: "220px" }}>
                      <span className="text-secondary small d-inline-block text-truncate" style={{ maxWidth: "200px" }} title={reasonStr}>
                        {reasonStr}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${
                        statusStr === "APPROVED" || statusStr === "CONFIRMED"
                          ? "bg-success"
                          : statusStr === "REJECTED"
                          ? "bg-danger"
                          : "bg-warning text-dark"
                      }`}>
                        {statusStr}
                      </span>
                    </td>
                    <td className="text-end pe-3">
                      <button
                        className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1"
                        title="View Details"
                        onClick={() => {
                          const session = JSON.parse(localStorage.getItem("user") || "{}");
                          const empId = String(item.empId || item.employeeNumber || user?.empId || user?.employeeNumber || user?.details?.emp_no || session.empId || session.employeeNumber || "").trim();
                          if (!empId) {
                            toast.error("Employee ID is unavailable. Please reload your profile.");
                            return;
                          }
                          const idParam = item.vacationIntimationId || item.vacationId || item.allotmentReferenceNumber || item.registrationReferenceNumber || item.id;
                          if (idParam) {
                            navigate(`${routes.staffQuarter.vacationDetails.replace(":id", encodeRouteParam(idParam))}?empId=${encodeURIComponent(empId.padStart(8, "0"))}`);
                          }
                        }}
                      >
                        <i className="ti ti-eye text-primary"></i> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const formContent = (
    <div>
      {/* Notice Header */}
      <div
        className="card border-0 shadow-sm mb-4"
        style={{ borderRadius: "14px", backgroundColor: "#ffffff" }}
      >
        <div className="card-body p-3.5 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center me-3 rounded-circle"
              style={{ width: "42px", height: "42px", backgroundColor: "rgba(13, 110, 253, 0.1)" }}
            >
              <i className="ti ti-info-circle text-primary" style={{ fontSize: '20px' }}></i>
            </div>
            <div>
              <h6 className="fw-bold mb-1 text-dark">Formal Quarter Vacation Notice</h6>
              <span className="small text-muted">
                Use this form to submit your formal intimation of vacating the allotted Staff Quarter.
              </span>
            </div>
          </div>
          {hasSubmittedIntimations && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm fw-bold px-3 d-flex align-items-center gap-1"
              onClick={() => setShowForm(false)}
            >
              <i className="ti ti-arrow-left"></i> View Submitted Intimations
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Card 1: Quarter Selection & Details */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px", backgroundColor: "#fff" }}>
          <div
            className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center"
            style={{ borderRadius: "14px 14px 0 0" }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-building-community text-primary" style={{ fontSize: '18px' }}></i>
              <h5 className="card-title mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>1. Allotted Quarter Information</h5>
            </div>
            {allotments.length > 1 && (
              <select
                className="form-select form-select-sm w-auto border-secondary-subtle"
                onChange={(e) =>
                  setSelectedAllotment(
                    allotments.find((a: any) => String(a.registrationId || a.id) === e.target.value)
                  )
                }
                value={selectedAllotment?.registrationId || selectedAllotment?.id}
              >
                {allotments.map((a: any) => (
                  <option key={a.registrationId || a.id} value={a.registrationId || a.id}>
                    Flat {formatFlatNoWithBlock(a)} ({a.location})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="card-body p-4">
            {allotments.length === 0 ? (
              <div
                className="p-3.5 rounded-3 d-flex align-items-center"
                style={{ backgroundColor: "rgba(255, 193, 7, 0.08)", border: "1px solid rgba(255, 193, 7, 0.25)", borderRadius: "12px" }}
              >
                <i className="ti ti-alert-triangle text-warning fs-3 me-3"></i>
                <div>
                  <h6 className="fw-bold mb-0 text-dark">No Active Allotment Found</h6>
                  <span className="small text-muted">There are no active quarter allotments registered under your employee profile.</span>
                </div>
              </div>
            ) : (
              <div className="row g-3">
                <div className="col-md-3 col-sm-6">
                  <div className="p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <span className="text-muted small d-block mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>Flat Number</span>
                    <span className="fw-bold text-primary d-flex align-items-center gap-1" style={{ fontSize: '15px' }}>
                      <i className="ti ti-home" style={{ fontSize: '16px' }}></i>
                      {formatFlatNoWithBlock(selectedAllotment) || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6">
                  <div className="p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <span className="text-muted small d-block mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>Quarter Type</span>
                    <span className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: '15px' }}>
                      <i className="ti ti-building" style={{ fontSize: '16px' }}></i>
                      {selectedAllotment?.quarterType || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6">
                  <div className="p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <span className="text-muted small d-block mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>Location</span>
                    <span className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: '15px' }}>
                      <i className="ti ti-map-pin" style={{ fontSize: '16px' }}></i>
                      {selectedAllotment?.location || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6">
                  <div className="p-3 rounded-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <span className="text-muted small d-block mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>Allotment Ref Number</span>
                    <span className="fw-bold text-secondary d-flex align-items-center gap-1" style={{ fontSize: '14px' }}>
                      <i className="ti ti-hash" style={{ fontSize: '15px' }}></i>
                      {selectedAllotment?.referenceNumber || selectedAllotment?.allotmentReferenceNumber || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Vacation Details */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px", backgroundColor: "#fff" }}>
          <div
            className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2"
            style={{ borderRadius: "14px 14px 0 0" }}
          >
            <i className="ti ti-calendar-event text-danger" style={{ fontSize: '18px' }}></i>
            <h5 className="card-title mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>2. Intimation & Vacation Details</h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold text-dark">
                  Actual / Intimated Date of Vacation <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="vacationDate"
                  value={form.vacationDate}
                  onChange={handleChange}
                  className="form-control shadow-none"
                  required
                />
                <small className="text-muted">Specify the date when quarter will be handed over.</small>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold text-dark">
                  Detailed Reason for Vacation <span className="text-danger">*</span>
                </label>
                <textarea
                  name="reasonDetails"
                  value={form.reasonDetails}
                  onChange={handleChange}
                  className="form-control shadow-none"
                  rows={3}
                  placeholder="Provide specific reason or details regarding vacation..."
                  required
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Employee Undertaking */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px", backgroundColor: "#fff" }}>
          <div
            className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2"
            style={{ borderRadius: "14px 14px 0 0" }}
          >
            <i className="ti ti-shield-check text-success" style={{ fontSize: '18px' }}></i>
            <h5 className="card-title mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>3. Employee Undertaking</h5>
          </div>
          <div className="card-body p-4">
            <div
              className="form-check p-3 rounded-3 d-flex align-items-start gap-3 mb-0"
              style={{ backgroundColor: "rgba(25, 135, 84, 0.04)", border: "1px solid rgba(25, 135, 84, 0.2)" }}
            >
              <input
                type="checkbox"
                id="undertaking"
                name="undertaking"
                checked={form.undertaking}
                onChange={handleChange}
                className="form-check-input mt-1"
                style={{ width: "1.25em", height: "1.25em", cursor: "pointer" }}
                required
              />
              <label className="form-check-label text-dark fw-semibold small" htmlFor="undertaking" style={{ cursor: "pointer" }}>
                I hereby intimate my vacation of the staff quarter and declare that all details supplied are true. I undertake to hand over the quarter in clean, damage-free condition along with all electrical & sanitary fittings intact.
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex align-items-center justify-content-end gap-3 mb-4">
          <button
            type="button"
            className="btn btn-outline-secondary px-4 fw-bold"
            onClick={() => (hasSubmittedIntimations ? setShowForm(false) : onSuccess ? onSuccess() : navigate(-1))}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
            disabled={isSubmitting || allotments.length === 0}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                Submitting Intimation...
              </>
            ) : (
              <>
                <i className="ti ti-send me-1"></i>
                Submit Vacation Intimation Form
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const formBody = isDisplayingTable ? tableView : formContent;

  if (embedded) {
    return formBody;
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 text-dark fw-bold">Vacation Request</h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard || "#"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.staffQuarter.overview || "#"}>Staff Quarters</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Vacation Request
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left me-1"></i>
              Back
            </button>
          </div>
        </div>

        {formBody}
      </div>
    </div>
  );
};

export { VacationIntimationForm };
export default VacationIntimationForm;
